/**
 * ルーム設定の集中管理ファイル
 *
 * このファイルでルームタイプ、設定項目、デフォルト値を一元管理します。
 * 新しいルームタイプや設定項目を追加する場合は、このファイルのみを更新してください。
 */

// ルームタイプの定義
export const ROOM_TYPES = [
  { title: "通常の部屋", value: "normal" },
  { title: "分子モデル", value: "molecule" },
  { title: "防災-ねこ車", value: "disaster_cart" },
  { title: "防災-消火器", value: "disaster_extinguisher" },
  { title: "シュークリームタワー", value: "cake_maker" },
  { title: "言葉を形に", value: "word_battle" },
  { title: "手の動きの記録", value: "hand_recorder" },
  { title: "見た目の記録", value: "eye_recorder" },
  { title: "建築", value: "architecture" },
  { title: "XGRIDS", value: "xgrids" },
  { title: "Gaussian_Splatting", value: "gaussian_splatting" },
  { title: "タイトック", value: "taitock" },
  { title: "タイトックシアター（視聴用）", value: "taitock_theater" },
  { title: "工場", value: "factory" },
  { title: "防災マップ", value: "disaster_map" },
  { title: "フィッシング", value: "fishing" },
  { title: "船舶・航空機マップ", value: "skysea_map" },
  { title: "テンプレート", value: "New" },
  { title: "ドミノート", value: "dominote" },
  { title: "美術館", value: "museum" },
  { title: "中世の街並みVR", value: "medieval" },
  { title: "VRコンテンツ", value: "contents" },
  { title: "MRガーデン", value: "garden" },
  { title: "プラネタリウム", value: "planetarium" },
  { title: "お絵かき（絵が動き出すMR）", value: "okaki" },
  { title: "サプライズ（お祝いショー）", value: "surprise" },
  // 空中に描いた線を 3D モデル化し、接続 / 分離 / 切断を行う部屋。
  // 機能の有効化は Unity 側が roomType を見て判断するため、poranos 側は選択肢のみ持つ。
  { title: "彫刻（空中に線を描く）", value: "sculpture" },
  // 対戦ゲーム。互いに風船を作って相手の風船を割り、最後に多く残した方が勝ち。
  // 大きい風船ほど有利だが割られやすい、というジレンマがルールの核。
  // ゲームロジックは Unity 側が roomType を見て有効化するため、poranos 側は選択肢のみ持つ。
  { title: "風船ゲーム（風船を割り合う）", value: "balloon" },
  // 魔法の箱を使うことで誰もが魔法を使えるようになる部屋。5 分の体験ツアー形式。
  // 箱とツアー進行は Unity 側が roomType を見て有効化するため、poranos 側は選択肢のみ持つ。
  // normal 部屋の enableMagic（魔法を使えるようにする）とは別物なので混同しないこと。
  { title: "魔法の箱（5分の魔法体験ツアー）", value: "magicbox" },
  // スポンジ / クリーム / フルーツを層状に積み上げ、コーティングやホイップ絞りで飾り、
  // 8 等分にカットして断面で層の重なりを確認する MR シミュレーター。
  // 既存の cake_maker（シュークリームタワー）とは別の部屋。
  // 進行は Unity 側が roomType を見て有効化するため、poranos 側は選択肢のみ持つ。
  { title: "ケーキデコレーター（ケーキ積層/断面）", value: "cake_decorator" },
];

// ビデオスクリーンタイプの定義
export const VIDEO_SCREEN_TYPES = [
  { title: "表示しない", value: "Off" },
  { title: "通常の2Dビデオ", value: "Video2D" },
  { title: "2面スクリーン", value: "Video2screens" },
  { title: "3面スクリーン", value: "Video3screens" },
  { title: "360度映像 (3DタイプA)", value: "Video3D_360_A" },
  { title: "360度映像 (3DタイプB)", value: "Video3D_360_B" },
  { title: "360度映像 (2D)", value: "Video2D_360" },
];

// ルーム設定のデフォルト値
export const DEFAULT_ROOM_SETTINGS = {
  roomType: "normal",
  isEternal: false,
  isAutoPlay: false,
  videoScreenType: "Off",
  enableMagic: false,
  enableGravity: false,
  autoScaleModel: false,
  openPassthroughWindow: false,
  enableModelConnect: false,
  enableModelCut: false,
};
// useAiNpc は Step 3-B（2026-08-03）でサーバー側ごと廃止した。
// 部屋で NPC を使うかは RoomBinding（シナリオの紐付け）で表現する。

// 設定項目の定義（UIラベルと条件付き表示の設定を含む）
export const ROOM_SETTING_FIELDS = [
  {
    key: "isEternal",
    label: "オブジェクトの位置を固定する",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "autoScaleModel",
    label: "3Dモデルを自動で拡大縮小する",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "enableMagic",
    label: "魔法を使えるようにする",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "isAutoPlay",
    label: "ビデオや立体映像を自動再生する",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "enableGravity",
    label: "重力を有効にする",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "enableModelConnect",
    label: "3Dモデル同士を接続できるようにする",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "enableModelCut",
    label: "3Dモデルをカットできるようにする",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal",
  },
  {
    key: "videoScreenType",
    label: "動画のスクリーン表示",
    type: "select",
    options: VIDEO_SCREEN_TYPES,
    showWhen: (settings) => settings.roomType === "normal" && settings.isAutoPlay,
  },
  {
    key: "openPassthroughWindow",
    label: "操作画面用の窓を開ける",
    type: "checkbox",
    showWhen: (settings) => settings.roomType === "normal" && settings.isAutoPlay,
  },
];

/**
 * APIレスポンスからルーム設定を正規化する
 * 不足しているフィールドにはデフォルト値を適用
 *
 * @param {Object} apiSettings - APIから取得した設定オブジェクト
 * @returns {Object} 正規化された設定オブジェクト
 */
export function normalizeRoomSettings(apiSettings = {}) {
  const normalized = createEmptySettings();

  // roomType の特別処理（enableMolecule 互換性維持）
  if (apiSettings.roomType) {
    normalized.roomType = apiSettings.roomType;
  } else if (apiSettings.enableMolecule) {
    normalized.roomType = "molecule";
  }

  // その他のフィールドをマージ
  Object.keys(DEFAULT_ROOM_SETTINGS).forEach((key) => {
    if (key !== "roomType" && apiSettings[key] !== undefined) {
      normalized[key] = apiSettings[key];
    }
  });

  return normalized;
}

/**
 * 設定オブジェクトの空コピーを作成
 *
 * @returns {Object} デフォルト値で初期化された設定オブジェクト
 */
export function createEmptySettings() {
  return { ...DEFAULT_ROOM_SETTINGS };
}

/**
 * ユーザー権限に基づいて許可されたルームタイプをフィルタリング
 *
 * @param {Array} allowedRoomTypes - 許可されたルームタイプのvalue配列（空配列=normalのみ）
 * @returns {Array} フィルタリングされたルームタイプ配列
 */
export function getFilteredRoomTypes(allowedRoomTypes = []) {
  // "normal"は常に含める
  const allowedSet = new Set([...(allowedRoomTypes || []), "normal"]);
  return ROOM_TYPES.filter((type) => allowedSet.has(type.value));
}

/**
 * ユーザー権限に基づいて許可されたルーム設定項目をフィルタリング
 *
 * @param {Array} allowedRoomSettings - 許可されたルーム設定のkey配列（空配列=すべて許可）
 * @returns {Array} フィルタリングされた設定項目配列
 */
export function getFilteredRoomSettingFields(allowedRoomSettings = []) {
  // 空配列の場合はすべて許可
  if (!allowedRoomSettings || allowedRoomSettings.length === 0) {
    return ROOM_SETTING_FIELDS;
  }
  return ROOM_SETTING_FIELDS.filter((field) => allowedRoomSettings.includes(field.key));
}

/**
 * ルーム設定項目のキーと日本語ラベルのマッピングを取得
 *
 * @returns {Object} key: label のマッピングオブジェクト
 */
export function getRoomSettingLabels() {
  const labels = {};
  ROOM_SETTING_FIELDS.forEach((field) => {
    labels[field.key] = field.label;
  });
  return labels;
}

/**
 * ルームタイプのvalueと日本語ラベルのマッピングを取得
 *
 * @returns {Object} value: title のマッピングオブジェクト
 */
export function getRoomTypeLabels() {
  const labels = {};
  ROOM_TYPES.forEach((type) => {
    labels[type.value] = type.title;
  });
  return labels;
}
